import { useEffect, useState } from 'react';
import {
  apiClient,
  ApiError,
  type ProjectActivityItem,
  type MemberRow,
} from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/components/UserAvatar';

interface Props {
  projectId: string;
  myUserId: string;
  /** Project members — used to resolve actor avatars. */
  members: MemberRow[];
}

const KIND_VERB: Record<ProjectActivityItem['kind'], string> = {
  task_created: 'created task',
  task_done: 'completed task',
  chat_posted: 'posted in chat',
  note_added: 'added a note',
  link_added: 'added a link',
};

const KIND_ICON: Record<ProjectActivityItem['kind'], string> = {
  task_created: '＋',
  task_done: '✓',
  chat_posted: '💬',
  note_added: '✎',
  link_added: '🔗',
};

const KIND_TINT: Record<ProjectActivityItem['kind'], string> = {
  task_created: 'text-muted-foreground',
  task_done: 'text-[color:var(--glow-color)]',
  chat_posted: 'text-muted-foreground',
  note_added: 'text-muted-foreground',
  link_added: 'text-muted-foreground',
};

/** Project activity timeline — synthesized from task/comment/note/link rows.
 *  Polls every 15s when visible. Newest first, capped at 100 items by the API. */
export function ProjectActivity({ projectId, myUserId, members }: Props) {
  const [items, setItems] = useState<ProjectActivityItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh(silent = false) {
    try {
      const r = await apiClient.listProjectActivity(projectId);
      setItems(r.items);
      if (!silent) setErr(null);
    } catch (e) {
      if (!silent) setErr(e instanceof ApiError ? e.message : 'Failed to load activity');
    }
  }

  useEffect(() => {
    refresh();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') refresh(true);
    }, 15000);
    function onVis() {
      if (document.visibilityState === 'visible') refresh(true);
    }
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
        {items === null ? (
          <div className="space-y-2" aria-busy="true">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-card" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="grid h-full place-items-center text-center text-xs text-muted-foreground/70">
            <div>
              <p className="mb-1 font-mono uppercase tracking-widest text-[10px]">
                No activity yet
              </p>
              <p>Tasks, notes, chat &amp; links will show up here as they happen.</p>
            </div>
          </div>
        ) : (
          <ol role="list" className="space-y-3">
            {items.map((it) => {
              const isMe = it.actor_user_id === myUserId;
              const author = it.actor_name || it.actor_email;
              const m = members.find((x) => x.user_id === it.actor_user_id);
              return (
                <li key={it.id} className="flex gap-3">
                  <div className="relative flex flex-col items-center">
                    <UserAvatar
                      email={it.actor_email}
                      image_key={m?.image_key}
                      avatar_url={m?.avatar_url}
                      size={24}
                      title={it.actor_email}
                    />
                    {/* Kind icon as a small badge on the bottom-right of
                        the avatar — preserves the at-a-glance "what happened"
                        signal while putting the WHO front and center. */}
                    <span
                      aria-hidden
                      className={cn(
                        'absolute -bottom-0.5 -right-1 grid size-3.5 place-items-center rounded-full border border-background bg-card font-mono text-[9px] leading-none',
                        KIND_TINT[it.kind],
                      )}
                    >
                      {KIND_ICON[it.kind]}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1 pb-1">
                    <div className="flex flex-wrap items-baseline gap-x-1.5 text-xs">
                      <span className={cn('font-semibold', isMe ? 'text-primary' : 'text-foreground')}>
                        {isMe ? 'You' : author}
                      </span>
                      <span className="text-muted-foreground">{KIND_VERB[it.kind]}</span>
                      <RelativeTime ts={it.timestamp} />
                    </div>
                    <p
                      className="mt-0.5 truncate text-xs text-muted-foreground/80"
                      title={it.target_label}
                    >
                      {it.target_label}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
      {err && (
        <div
          role="alert"
          className="border-t border-destructive/40 bg-destructive/10 px-4 py-2 text-xs text-foreground"
        >
          {err}
        </div>
      )}
    </div>
  );
}

/** Hover-tooltip exact timestamp + relative-time label.
 *  Click toggles between relative and absolute (sticky toggle, not just hover). */
export function RelativeTime({ ts }: { ts: number }) {
  const [showAbs, setShowAbs] = useState(false);
  const abs = new Date(ts).toLocaleString();
  const rel = timeShort(ts);
  return (
    <button
      type="button"
      onClick={() => setShowAbs((v) => !v)}
      title={abs}
      className="cursor-pointer rounded font-mono text-[10px] tabular-nums text-muted-foreground/60 transition-colors hover:text-foreground"
      aria-label={`Posted ${abs}`}
    >
      {showAbs ? abs : rel}
    </button>
  );
}

function timeShort(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
